import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Phone, 
  MessageSquare, 
  Mail, TrendingUp, 
  Clock,Bot,
  PhoneCall, 
  Activity,
  Loader2
} from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { Call, OmnichannelMessage } from '../../types';
import { Headphones } from 'lucide-react';
import LiveCallCard from './LiveCallCard';
import MetricCard from './MetricCard';
import TabButton from '../shared/TabButton';
import CallHistoryTab from './CallHistoryTab';
import MessagingTab from './MessagingTab';
import LiveCallMonitor from './LiveCallMonitor';
import { useActiveCalls } from '../../hooks/useActiveCalls';
import { useRealtimeTranscript } from '../../hooks/useRealtimeTranscript';
import { useDebounce } from '../../hooks/useDebounce';
import { useGetSMS } from '../../services/query/messaging/sms';
import { useGetEmails } from '../../services/query/messaging/email';
import { useGetAllCalls } from '../../services/query/calls/callsQueries';
import { AllCall } from '../../services/api/calls/callsApi';

type TabType = 'calls' | 'sms' | 'email';

export default function ContactCentre() {
  const { state } = useAppContext();
  const { calls, omnichannelMessages } = state;

  const [activeTab, setActiveTab] = useState<TabType>('calls');
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [debouncedCallSearchTerm, setCallSearchTerm] = useDebounce<string | undefined>(undefined, 300);
  const [debouncedMessageSearchTerm, setMessageSearchTerm] = useDebounce<string | undefined>(undefined, 300);
  const [selectedLiveCall, setSelectedLiveCall] = useState<Call | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);

  const [currentTime, setCurrentTime] = useState(Date.now());

    

  const { 
    activeCalls, 
    isConnected: isSocketConnected, 
    refreshActiveCalls 
  } = useActiveCalls();

  const nameParam = typeof debouncedCallSearchTerm === 'string' && debouncedCallSearchTerm.trim() !== ''
    ? debouncedCallSearchTerm.trim()
    : undefined;

  const {data: allCallData, isLoading: isLoadingCalls} = useGetAllCalls({ 
    name: nameParam,
    page: currentPage, 
    limit 
  });

  const transcriptHook = useRealtimeTranscript(true);

  const ongoingCalls: Call[] = useMemo(() => {
    return activeCalls.map(apiCall => ({
      id: apiCall.call_id,
      patientId: apiCall.caller_id.toString(),
      patientName: apiCall.detected_user_name || 'Unknown Patient',
      startTime: new Date(apiCall.start_time),
      status: 'connected' as const,
      type: 'inbound' as const,
      purpose: 'appointment',
      urgencyLevel: 'medium',
      messages: [] // Will be loaded in real-time via Socket.IO in LiveCallMonitor
    }));
  }, [activeCalls]);

  const { data: smsApi } = useGetSMS();
  const { data: emailApi } = useGetEmails();

  const smsMessages = smsApi?.data ?? omnichannelMessages.filter(m => m.channel === 'sms');
  const emailMessages = emailApi?.data ?? omnichannelMessages.filter(m => m.channel === 'email');

  const groupMessagesByPatient = (messages: OmnichannelMessage[]) => {
    const grouped = messages.reduce((acc, msg) => {
      if (!acc[msg.patientId]) {
        acc[msg.patientId] = [];
      }
      acc[msg.patientId].push(msg);
      return acc;
    }, {} as Record<string, OmnichannelMessage[]>);

    return Object.entries(grouped).map(([patientId, msgs]) => ({
      patientId,
      patientName: msgs[0].patientName,
      messages: msgs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
      lastMessage: msgs[msgs.length - 1],
      // unreadCount: msgs.filter(m => m.direction === 'inbound' && m.status !== 'read').length
    }));
  };

  const smsConversations = useCallback(() => groupMessagesByPatient(smsMessages), [smsMessages])();
  const emailConversations = useCallback(() => groupMessagesByPatient(emailMessages), [emailMessages])();

    useEffect(() => {
      const interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);

      return () => clearInterval(interval);
    }, []);

    const getCallDuration = (call: Call) => {
      const seconds = Math.floor((currentTime - new Date(call.startTime).getTime()) / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

  const totalCalls = calls.length;
  const todayCalls = calls.filter(c => {
    const callDate = new Date(c.startTime).toDateString();
    const today = new Date().toDateString();
    return callDate === today;
  }).length;

  const avgCallDuration = calls.reduce((acc, call) => {
    return acc + (call.duration || 0);
  }, 0) / calls.length || 0;

  const aiResolvedCalls = calls.filter(c =>
    c.messages.every(m => m.speaker === 'ai' || m.speaker === 'patient')
  ).length;

  const resolvedPercentage = totalCalls > 0 ? Math.round((aiResolvedCalls / totalCalls) * 100) : 0;

  const callsByHour = Array.from({ length: 24 }, (_, hour) => {
    const count = calls.filter(call => {
      const callHour = new Date(call.startTime).getHours();
      return callHour === hour;
    }).length;
    return { hour, count };
  });

  const maxCallsInHour = Math.max(...callsByHour.map(h => h.count), 1);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Headphones className="w-7 h-7 mr-3 text-green-600" />
              Contact Centre
            </h1>
            <p className="text-gray-600 mt-1">Unified communication hub for all patient interactions</p>
          </div>
          {ongoingCalls.length > 0 ? (<div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-700">Live</span>
          </div>): (<div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
            <span className="text-sm font-medium text-gray-500">No Live Calls</span>
          </div>)}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <MetricCard
            icon={<PhoneCall className="w-6 h-6 text-blue-600" />}
            label="Total Calls Today"
            value={todayCalls.toString()}
            subtext={`${totalCalls} total`}
            color="blue"
          />
          <MetricCard
            icon={<Clock className="w-6 h-6 text-purple-600" />}
            label="Avg Call Duration"
            value={`${Math.round(avgCallDuration / 60)}m`}
            subtext={`${Math.round(avgCallDuration)}s total`}
            color="purple"
          />
          <MetricCard
            icon={<Bot className="w-6 h-6 text-green-600" />}
            label="AI Resolution Rate"
            value={`${resolvedPercentage}%`}
            subtext={`${aiResolvedCalls} automated`}
            color="green"
          />
          <MetricCard
            icon={<TrendingUp className="w-6 h-6 text-orange-600" />}
            label="Response Time"
            value="<30s"
            subtext="Average response"
            color="orange"
          />
        </div>

        {/* Call Volume Graph */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <Activity className="w-4 h-4 mr-2 text-gray-600" />
              Call Volume by Hour
            </h3>
            <span className="text-xs text-gray-500">Last 24 hours</span>
          </div>
          <div className="flex items-end space-x-1 h-24">
            {callsByHour.map((item) => {
              const height = maxCallsInHour > 0 ? (item.count / maxCallsInHour) * 100 : 0;
              const isCurrentHour = item.hour === new Date().getHours();

              return (
                <div key={item.hour} className="flex-1 flex flex-col items-center">
                  <div className="w-full flex items-end justify-center" style={{ height: '80px' }}>
                    {item.count > 0 && (
                      <div
                        className={`w-full rounded-t transition-all ${
                          isCurrentHour ? 'bg-green-500' : 'bg-blue-500 hover:bg-blue-600'
                        }`}
                        style={{ height: `${height}%` }}
                        title={`${item.hour}:00 - ${item.count} calls`}
                      ></div>
                    )}
                  </div>
                  {item.hour % 3 === 0 && (
                    <span className="text-xs text-gray-500 mt-1">{item.hour}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Ongoing/Live Calls */}
      {!isSocketConnected ? (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <span className="ml-3 text-gray-600">Connecting to live call feed...</span>
          </div>
        </div>
      ) : ongoingCalls.length > 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center">
              <Phone className="w-5 h-5 mr-2 text-green-600" />
              Live Calls
              <span className="ml-3 px-2 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full">
                {ongoingCalls.length} Active
              </span>
            </h2>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Live via WebSocket</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ongoingCalls.map((call) => (
              <LiveCallCard
                getCallDuration={getCallDuration}
                key={call.id}
                call={call}
                onClick={() => setSelectedLiveCall(call)}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <Phone className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">No active calls at the moment</p>
            <p className="text-xs mt-1">Live calls will appear here when patients call in</p>
          </div>
        </div>
      )}

      {/* Live Call Monitor Modal */}
      {selectedLiveCall && (
        <LiveCallMonitor
          getCallDuration={getCallDuration}
          call={selectedLiveCall}
          transcriptHook={transcriptHook}
          onClose={() => {
            setSelectedLiveCall(null);
            refreshActiveCalls();
          }}
        />
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <TabButton
              active={activeTab === 'calls'}
              onClick={() => setActiveTab('calls')}
              icon={<Phone className="w-5 h-5" />}
              label="Call History"
              count={allCallData?.pagination.total_items}
            />
            <TabButton
              active={activeTab === 'sms'}
              onClick={() => setActiveTab('sms')}
              icon={<MessageSquare className="w-5 h-5" />}
              label="SMS"
              count={smsConversations.length}
              badge={undefined}
              // badge={smsConversations.reduce((acc, c) => acc + c.unreadCount, 0)}
            />
            <TabButton
              active={activeTab === 'email'}
              onClick={() => setActiveTab('email')}
              icon={<Mail className="w-5 h-5" />}
              label="Email"
              count={emailConversations.length}
              badge={undefined}
              // badge={emailConversations.reduce((acc, c) => acc + c.unreadCount, 0)}
            />
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Calls Tab */}
          {activeTab === 'calls' && (
            <CallHistoryTab 
            calls={allCallData?.data as AllCall[] | undefined}
            pagination={allCallData?.pagination}
            isLoading={isLoadingCalls}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            searchTerm={debouncedCallSearchTerm} 
            setSearchTerm={setCallSearchTerm} />
          )}

          {/* SMS Tab */}
          {activeTab === 'sms' && (
            <MessagingTab
              conversations={smsConversations}
              selectedConversation={selectedConversation}
              setSelectedConversation={setSelectedConversation}
              searchTerm={debouncedMessageSearchTerm}
              setSearchTerm={setMessageSearchTerm}
              type="sms"
            />
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <MessagingTab
              conversations={emailConversations}
              selectedConversation={selectedConversation}
              setSelectedConversation={setSelectedConversation}
              searchTerm={debouncedMessageSearchTerm}
              setSearchTerm={setMessageSearchTerm}
              type="email"
            />
          )}
        </div>
      </div>
    </div>
  );
}






